// Run files + artifacts.json manifest for the Artifacts tab (A1EE-411).
// The manifest is fetched THROUGH the content endpoint (files/artifacts.json) —
// there is no separate links endpoint; a 404 (file never produced — Bedrock
// failure / no transcript) is a normal empty state, not an error.
//
// Query staleness is HEADER-DRIVEN (A1EE-438): the server stamps
// `Cache-Control: …immutable` on terminal-run payloads and `no-store` on
// not-ready 404s, and these hooks map that to staleTime Infinity vs 0. The
// server is the single source of caching truth — a "not yet" answer (incl. the
// synthesized empty manifest) goes stale immediately and refetches on the next
// mount, so it can never outlive reality.
import { useQuery } from '@tanstack/react-query';
import { ApiError } from './client';
import { responseFetch } from './responseFetch';
import { queryKeys } from './queryKeys';
import type { RunFileEntry, RunArtifactsManifest } from '../types';

// Empty manifest returned on a 404 (artifacts.json never produced — Bedrock
// failure / no transcript). Shared so the fallback doesn't restate the literal.
// Deep-frozen: it's a shared singleton handed to the query cache, so the object
// AND its `artifacts` array are frozen — a consumer pushing onto the array
// would otherwise corrupt every subsequent reader off the same query.
// (The manifest's `artifacts` field is `readonly`, so the frozen array assigns clean.)
export const EMPTY_MANIFEST: RunArtifactsManifest = Object.freeze({
  schemaVersion: 1,
  artifacts: Object.freeze([]),
});

// Internal cache envelope: the payload plus whether the server declared it
// immutable. Stored raw in the query cache (staleTime reads it); `select`
// unwraps so consumers only ever see the payload.
type CachePolicied<T> = { value: T; immutable: boolean };

async function fetchWithCachePolicy<T>(path: string): Promise<CachePolicied<T>> {
  const response = await responseFetch(path, { headers: { Accept: 'application/json' } });
  // Regression guard (A1EE-323): CloudFront's SPA fallback is a viewer-request
  // function scoped to the S3 behavior, so /api/* errors are NOT rewritten to
  // index.html today — but a re-added distribution-wide custom_error_response
  // would silently mask them as 200 HTML. Reject non-JSON 2xx so that surfaces
  // as a typed error, not a JSON parse failure (same guard apiFetch applies;
  // we bypass apiFetch here to read headers).
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new ApiError(
      response.status,
      await response.text(),
      `expected JSON, got "${contentType}"`,
    );
  }
  const value = (await response.json()) as T;
  const cacheControl = response.headers.get('Cache-Control') ?? '';
  return { value, immutable: cacheControl.includes('immutable') };
}

// staleTime as a function of the cached envelope (TanStack v5): immutable
// payloads never go stale; everything else (incl. an absent header) is stale
// immediately and refetches on the next mount.
function staleTimeFromPolicy(query: { state: { data?: CachePolicied<unknown> } }): number {
  return query.state.data?.immutable ? Infinity : 0;
}

export function runFilesUrl(jobId: string, runId: string): string {
  // Encode the path params (sibling idiom; ids come from useParams). See runJournal.ts.
  return `/api/v1/jobs/${encodeURIComponent(jobId)}/runs/${encodeURIComponent(runId)}/files`;
}

export function runFileContentUrl(jobId: string, runId: string, name: string): string {
  return `${runFilesUrl(jobId, runId)}/${encodeURIComponent(name)}`;
}

export function useRunFiles(jobId: string, runId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.runFiles(jobId, runId),
    queryFn: async (): Promise<CachePolicied<RunFileEntry[]>> => {
      try {
        return await fetchWithCachePolicy<RunFileEntry[]>(runFilesUrl(jobId, runId));
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          // A run with no S3 file listing yet — an interactive session that
          // produced nothing (A1EE-574), or files not landed — 404s the listing.
          // Normal empty state, not an error. Non-immutable so a later mount
          // re-checks if files land (same shape as the manifest 404 fallback).
          return { value: [], immutable: false };
        }
        throw e;
      }
    },
    enabled,
    staleTime: staleTimeFromPolicy,
    select: (envelope) => envelope.value,
  });
}

export function useRunArtifactsManifest(jobId: string, runId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.runArtifacts(jobId, runId),
    queryFn: async (): Promise<CachePolicied<RunArtifactsManifest>> => {
      try {
        return await fetchWithCachePolicy<RunArtifactsManifest>(
          runFileContentUrl(jobId, runId, 'artifacts.json'),
        );
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          // Non-immutable by construction: the file may land moments from now
          // (post-terminal summary/artifacts upload), so this empty state goes
          // stale immediately and re-checks on the next mount.
          return { value: EMPTY_MANIFEST, immutable: false };
        }
        throw e;
      }
    },
    enabled,
    staleTime: staleTimeFromPolicy,
    select: (envelope) => envelope.value,
  });
}

// Lazy fetch of summary.md as raw markdown for the Artifacts tab's inline
// disclosure (SummaryFileRow). Caller controls `enabled` (the disclosure's
// open state), so nothing is fetched until first expanded. Unlike the listing/
// manifest hooks above, staleness is not header-driven: a summary.md that
// exists is a static file, so the body is cached forever once fetched
// (staleTime: Infinity — same shape as useRunJournal).
export function useRunSummary(jobId: string, runId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.runSummary(jobId, runId),
    queryFn: async () => {
      const resp = await responseFetch(runFileContentUrl(jobId, runId, 'summary.md'));
      // Regression guard (A1EE-323): /api/* errors are not SPA-rewritten today
      // (viewer-request function, S3 behavior only), but a re-added distribution-wide
      // custom_error_response would return index.html on a 2xx. Reject HTML so we
      // surface a typed error instead of rendering the SPA shell as a summary.
      const ct = resp.headers.get('Content-Type') ?? '';
      if (ct.includes('text/html')) {
        throw new ApiError(resp.status, await resp.text(), `summary returned HTML: "${ct}"`);
      }
      return resp.text();
    },
    enabled,
    staleTime: Infinity,
  });
}
